import { useMemo } from 'react';
import { Typography, useTheme } from '@mui/material';
import {
  Card,
  ChartTooltip,
  Flex,
  FlexColumn,
} from '@elementary/common-ui/components';
import { numberWithCommas } from '@elementary/common-ui/utils/common';
import dayjs from 'dayjs';
import Humanize from 'humanize-plus';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Rectangle,
  RectangleProps,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis,
} from 'recharts';
import { themeTokens } from '@elementary/common-ui/style/tokens';
import { fontSize, fontWeight } from '@elementary/common-ui/style/utils';
import { SpendDatum, VisibleRunTypes } from './types';
import {
  MonthGroup,
  fillDailyGaps,
  groupSpendByMonth,
  isMultiMonth,
} from './spendChartUtils';

const LegendDot = ({ color, label }: { color: string; label: string }) => (
  <Flex sx={{ alignItems: 'center', gap: 0.75 }}>
    <span
      style={{
        width: 10,
        height: 10,
        borderRadius: themeTokens.radius.round,
        background: color,
      }}
    />
    <Typography sx={{ fontSize: fontSize('md'), color: 'content.secondary' }}>
      {label}
    </Typography>
  </Flex>
);

const TooltipRow = ({
  color,
  label,
  value,
}: {
  color: string;
  label: string;
  value: number;
}) => (
  <Flex sx={{ alignItems: 'center', gap: 1 }}>
    <span
      style={{
        width: 10,
        height: 10,
        borderRadius: themeTokens.radius.round,
        background: color,
      }}
    />
    <Typography>
      {label}: {numberWithCommas(Math.round(value))}
    </Typography>
  </Flex>
);

const CustomTooltip = ({
  active,
  payload,
  label,
}: TooltipProps<number, string>) => {
  const theme = useTheme();
  if (!active || !payload?.length) {
    return null;
  }
  const hasAgent = payload.some((p) => p.dataKey === 'agent');
  const hasCi = payload.some((p) => p.dataKey === 'ci');
  const agent = payload.find((p) => p.dataKey === 'agent')?.value ?? 0;
  const ci = payload.find((p) => p.dataKey === 'ci')?.value ?? 0;
  return (
    <ChartTooltip>
      <FlexColumn sx={{ gap: 1 }}>
        <Typography sx={{ fontWeight: fontWeight('medium') }}>
          {dayjs(label).format('MMM D, YYYY')}
        </Typography>
        {hasAgent && (
          <TooltipRow
            color={theme.palette.accent.selected}
            label="Agent"
            value={agent}
          />
        )}
        {hasCi && (
          <TooltipRow
            color={theme.palette.accent.primary}
            label="CI"
            value={ci}
          />
        )}
      </FlexColumn>
    </ChartTooltip>
  );
};

const TOP_RADIUS: [number, number, number, number] = [4, 4, 0, 0];

type AgentBarShapeProps = RectangleProps & {
  payload?: { ci?: number };
  showCi?: boolean;
};

const AgentBarShape = ({ showCi, ...props }: AgentBarShapeProps) => (
  <Rectangle {...props} radius={showCi && props.payload?.ci ? 0 : TOP_RADIUS} />
);

const MonthHeaderLabel = ({
  viewBox,
  group,
}: {
  viewBox?: { x: number; y: number };
  group: MonthGroup;
}) => {
  const theme = useTheme();
  if (!viewBox) {
    return null;
  }
  const { x, y } = viewBox;
  return (
    <g textAnchor="middle">
      <text
        x={x}
        y={y - 40}
        fill={theme.palette.icon.secondary}
        fontSize={12}
        fontWeight={600}
      >
        {group.label}
      </text>
      <text x={x} y={y - 26} fill={theme.palette.icon.primary} fontSize={11}>
        {Humanize.compactInteger(Math.round(group.total), 1)}
      </text>
      {group.forecast != null && (
        <text
          x={x}
          y={y - 13}
          fill={theme.palette.accent.selected}
          fontSize={11}
        >
          EOM ~{Humanize.compactInteger(Math.round(group.forecast), 1)}
        </text>
      )}
    </g>
  );
};

type Props = {
  data: SpendDatum[];
  visibleTypes: VisibleRunTypes;
};

const AiSpendByDayChart = ({ data, visibleTypes }: Props) => {
  const theme = useTheme();
  const chartData = useMemo(() => fillDailyGaps(data), [data]);
  const monthGroups = useMemo(() => groupSpendByMonth(chartData), [chartData]);
  const hasMultipleMonths = isMultiMonth(monthGroups);

  return (
    <Card>
      <FlexColumn sx={{ gap: 2, width: '100%' }}>
        <Flex sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography
            sx={{
              fontWeight: fontWeight('semibold'),
              fontSize: fontSize('lg'),
            }}
          >
            Daily credit spend
          </Typography>
          <Flex sx={{ gap: 2 }}>
            <LegendDot color={theme.palette.accent.selected} label="Agent" />
            <LegendDot color={theme.palette.accent.primary} label="CI" />
          </Flex>
        </Flex>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart
            data={chartData}
            margin={{ top: 54, right: 16, left: 0, bottom: 0 }}
            barCategoryGap={chartData.length > 120 ? 0 : 1}
          >
            <CartesianGrid strokeOpacity={0.5} vertical={false} />
            <XAxis
              dataKey="day"
              interval={0}
              tick={false}
              tickLine={false}
              axisLine={false}
              height={1}
            />
            <YAxis
              width={48}
              tickLine={false}
              axisLine={false}
              tickFormatter={(value: number) =>
                Humanize.compactInteger(value, 1)
              }
            />
            <Tooltip
              content={<CustomTooltip />}
              cursor={{ fillOpacity: 0.1 }}
            />

            {monthGroups.map((group) => (
              <ReferenceLine
                key={`hdr-${group.key}`}
                x={group.midDay}
                stroke="none"
                label={<MonthHeaderLabel group={group} />}
                ifOverflow="extendDomain"
              />
            ))}

            {hasMultipleMonths &&
              monthGroups
                .slice(1)
                .map((group) => (
                  <ReferenceLine
                    key={`div-${group.key}`}
                    x={group.firstDay}
                    stroke={theme.palette.chart.grid}
                    strokeDasharray="4 4"
                  />
                ))}

            {visibleTypes.agent && (
              <Bar
                dataKey="agent"
                name="Agent"
                stackId="spend"
                fill={theme.palette.accent.selected}
                shape={<AgentBarShape showCi={visibleTypes.ci} />}
              />
            )}
            {visibleTypes.ci && (
              <Bar
                dataKey="ci"
                name="CI"
                stackId="spend"
                fill={theme.palette.accent.primary}
                radius={TOP_RADIUS}
              />
            )}
          </BarChart>
        </ResponsiveContainer>
      </FlexColumn>
    </Card>
  );
};

export default AiSpendByDayChart;
